import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  ManageBackupsInfo,
  MessageLogsForm,
  MessageLogsInfo,
} from "IF/interfaceType";
import axios from "axios";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import { UserRole, useSessionStorage } from "IF/userSession";
import { MessageModal } from "./MessageModal";
import ConfirmationModal from "./ConfirmationModal";
import { DataContext } from "Contexts/DataProvider";
import ManageBackupsTable from "./ManageBackupsTable";
import ManageBackupsUploadDialog from "./ManageBackupsUploadDialog";
import { createTitle } from "Utils/format";
import ManageBackupsParts from "./ManageBackupsParts";
import MessageLogsTable from "./MessageLogsTable";
//import { theme } from "Theme/theme";
export const MessageLogsPage = () => {
  // ユーザ情報
  const { getUserData } = useSessionStorage();
  const userData = getUserData();
  const { id: userId, companyCode, userRole } = userData;

  // 検索フォーム初期値
  const initForm: MessageLogsForm = {
    schema: companyCode,
    sentAt: "",
    deliveryType: "",
    notificationType: "",
  };

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { menuItems, setChoiceMenu, toggleMenu } = sidebarContext;

  // 検索フォーム
  const [searchForm, setSearchForm] = useState<MessageLogsForm>(initForm);
  // 取得結果
  const [data, setData] = useState<MessageLogsInfo[]>([]);

  const [isCheck, setIsCheck] = useState(false);

  // レイアウト設定
  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  const [tableBodyWidth, setTableBodyWidth] = useState(0);
  const disabledButtonStyle = {
    color: "white",
    opacity: 0.6,
  };
  const theme = useTheme();

  // モーダル
  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
  };

  // 確認モーダル
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<React.ReactNode>("");
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const handleConfirmModalClose = () => setConfirmModalOpen(false);
  const handleConfirmModalOpen = (
    message: React.ReactNode,
    confirmAction: () => void
  ) => {
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  // 送信内容表示用ダイアログ
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBody, setSelectedBody] = useState<string | null>(null);

  // タイトル作成
  const title = "メール・SMS送信履歴";
  useEffect(() => {
    const index = UserRole.YKC !== userRole ? 3 : 4;
    document.title = createTitle(title);
    setChoiceMenu(title);
    toggleMenu("設定");
    if (UserRole.YKC === userRole) {
      toggleMenu(menuItems[index]?.subItems?.[2]?.text ?? "");
    }
  }, [userRole, menuItems, setChoiceMenu, toggleMenu]);

  // 初期表示時処理
  useEffect(() => {
    const handleResize = () => {
      let windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth - 52;
      const width = window.innerWidth;
      if (width < 600) {
        windowHeight = windowHeight - 180; // xs
      } else if (width < 900) {
        windowHeight = windowHeight - 190; // sm
      } else if (width < 1200) {
        windowHeight = windowHeight - 145; // md
      } else {
        windowHeight = windowHeight - 145; // lg
      }

      setTableBodyHeight(windowHeight);
      setTableBodyWidth(windowWidth);
    };

    // 初期表示時とリサイズ時に高さを計算
    handleResize();
    window.addEventListener("resize", handleResize);

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // 初期表示データ取得
    getData(searchForm, companyCode);
  }, [searchForm, companyCode]);

  useEffect(() => {
    const hasCheckedItems = data.some((item) => item.check);
    setIsCheck(hasCheckedItems);
  }, [data]);

  // データ取得
  const getData = async (form: MessageLogsForm, schema: string) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/MessageLogs/GetMessageLogs`,
        {
          params: {
            ...form,
            schema: schema,
          },
        }
      );
      setData(response.data);
    } catch (error) {
      setModalOpen(true);
      setMessage("メール/SMS送信履歴情報の取得に失敗しました");
      console.error("メール/SMS送信履歴情報の取得に失敗しました:", error);
    }
  };

  // 削除処理
  const handleDeleteChecked = async () => {
    const checkedFiles = data
      .filter((item) => item.check)
      .map((item) => item.id);

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/MessageLogs/DeleteMessageLogs`,
        {
          headers: { "Content-Type": "application/json" },
          data: {
            schema: companyCode,
            checkedLogs: checkedFiles,
          },
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("選択した送信履歴を削除しました。");
        getData(searchForm, companyCode);
      } else {
        throw new Error(
          `送信履歴の削除に失敗しました。サーバーからの応答: ${response.status} ${response.statusText}`
        );
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("送信履歴の削除に失敗しました。");
        console.error("送信履歴の削除に失敗しました。", error);
      }
    }
  };

  // チェックボックスの状態が変更されたときの処理
  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    const { checked } = event.target;
    setData((prevData) =>
      prevData.map((row) => (row.id === id ? { ...row, check: checked } : row))
    );
  };

  // 全てチェック/チェック解除のハンドラ
  const handleCheckAll = (checkedData: MessageLogsInfo[], checked: boolean) => {
    const targetIds = new Set(checkedData.map((row) => row.id));

    const updated = data.map((row) =>
      targetIds.has(row.id) ? { ...row, check: checked } : row
    );

    setData(updated);
  };

  return (
    <>
      <MessageModal
        open={isModalOpen}
        message={message}
        handleClose={closeModal}
      />
      <ConfirmationModal
        open={confirmModalOpen}
        message={confirmMessage}
        handleClose={handleConfirmModalClose}
        onConfirm={onConfirm!}
      />
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            width: { xs: "50vw", md: "30vw", lg: "30vw" },
            maxWidth: { xs: "50vw", md: "30vw", lg: "30vw" },
            backgroundColor: theme.palette.custom.sForm,
          },
        }}
      >
        <DialogTitle>本文</DialogTitle>
        <DialogContent dividers>
          {selectedBody && (
            <Box>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {selectedBody}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: {
            xs: 1,
            md: 0.5,
          },
          marginBottom: 1,
          marginTop: 0.1,
          paddingTop: 0,
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "#DC143C" }}
          sx={{
            width: {
              xs: "100%",
              md: "20vh",
            },
            "&.Mui-disabled": disabledButtonStyle,
          }}
          disabled={!isCheck}
          onClick={() =>
            handleConfirmModalOpen("選択した送信履歴を削除しますか？", () =>
              handleDeleteChecked()
            )
          }
        >
          削除
        </Button>
        {/* </Box> */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mt: 0.3,
          }}
        >
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                {/* 総ファイル数 */}
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="center"
                    size="small"
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: "fontWeightRegular",
                      backgroundColor: theme.palette.custom.tableHeader,
                      color: "white",
                      px: 1,
                      py: 0.5,
                      minWidth: "100px",
                    }}
                  >
                    履歴総数
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: "fontWeightRegular",
                      backgroundColor: theme.palette.custom.row,
                      textAlign: "center",
                      minWidth: "100px",
                    }}
                    size="small"
                  >
                    {data.length}件
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <MessageLogsTable
        data={data}
        tableBodyHeight={tableBodyHeight}
        tableBodyWidth={tableBodyWidth}
        handleCheckboxChange={handleCheckboxChange}
        handleCheckAll={handleCheckAll}
        setOpenDialog={setOpenDialog}
        setSelectedBody={setSelectedBody}
      />
    </>
  );
};
export default MessageLogsPage;
